import React from 'react'

import ColumnGridPanel from 'components/ColumnGridPanel'

import ConfigDropdown from 'pages/Device/SystemConfigPanel/ConfigDropdown'

import { SystemConfigReport, SystemConfiguration } from 'types/SystemConfigReport'

import { feet, friendlyName, percentage } from 'helpers/formatters'
import { scrubNumber } from 'helpers/inputCleaners'
import { hasValue } from 'helpers/reportFields'
import {
  ELECTRIC_HEAT_KW,
  INDOOR_UNIT_COIL_TONNAGE,
  GAS_FURNACE_BTUH_PER_STAGE,
  GAS_FURNACE_EFFICIENCY,
  CONTROL_TYPE,
  AIRFLOW_INPUT_MODE,
  INDOOR_STAGES,
  INDOOR_UNIT_TYPE,
  BLOWER_TYPE
} from 'helpers/reportFriendlyNames'
export type Props = Partial<SystemConfigReport> & {
  isEditing: boolean
  autoFocus: boolean
  onChangeCallback: (arg: Partial<SystemConfiguration>) => void
}

const IndoorUnit = ({ isEditing, autoFocus = false, onChangeCallback, ...report }: Props) => {
  const idUnitCoilTonnageOptions = [...INDOOR_UNIT_COIL_TONNAGE.map((coilTonnage) => [{ value: coilTonnage, name: friendlyName(coilTonnage) }])]
  const gasFurnaceBtuhPerStageOptions = [...GAS_FURNACE_BTUH_PER_STAGE.map((gasFurnaceBtuhPerStage) => [{ value: gasFurnaceBtuhPerStage, name: friendlyName(gasFurnaceBtuhPerStage) }])]
  const gasFurnaceEfficiencyOptions = [...GAS_FURNACE_EFFICIENCY.map((gasFurnaceEfficiency) => [{ value: gasFurnaceEfficiency, name: friendlyName(gasFurnaceEfficiency) }])]
  const electricHeatKWOptions = [...ELECTRIC_HEAT_KW.map((electricHeatKW) => [{ value: electricHeatKW, name: friendlyName(electricHeatKW) }])]
  const anyFurnace = report.hasS8ModelFurnace || report.hasS9ModelFurnace

  return (
    <ColumnGridPanel.Column>
      <ColumnGridPanel.Column.Header>Indoor Unit</ColumnGridPanel.Column.Header>
      <ColumnGridPanel.Column.Item label="ID Unit Type" value={friendlyName(report.indoorUnitType, INDOOR_UNIT_TYPE)} />
      <ColumnGridPanel.Column.Item label="ID Unit Stages" value={friendlyName(report.indoorStage, INDOOR_STAGES)} />
      <ColumnGridPanel.Column.Item label="Blower Type" value={friendlyName(report.blowerType, BLOWER_TYPE)} />

      <ColumnGridPanel.Column.Item label="Indoor Unit/Coil Refrig Tonnage"
        value={<ConfigDropdown isEditing={isEditing} autoFocus={autoFocus} options={idUnitCoilTonnageOptions} value={report.indoorUnitCoilTonnage}
                onChangeCallback={(value) => onChangeCallback({ indoorUnitCoilTonnage: scrubNumber(value, true) })}
               />}
      />

      <ColumnGridPanel.Column.Item label="Gas Furnace input BTU"
        value={<ConfigDropdown isEditing={isEditing} options={gasFurnaceBtuhPerStageOptions} value={report.gasFurnaceBtuhPerStage}
                onChangeCallback={(value) => onChangeCallback({ gasFurnaceBtuhPerStage: value as string })}
               />}
      />

      <ColumnGridPanel.Column.Item label="Gas Furnace Efficiency"
        value={<ConfigDropdown isEditing={isEditing} options={gasFurnaceEfficiencyOptions} value={report.gasFurnaceEfficiency}
                onChangeCallback={(value) => onChangeCallback({ gasFurnaceEfficiency: value as string })}
               />}
      />

      <ColumnGridPanel.Column.Item label="Elect Heat KW"
        value={<ConfigDropdown isEditing={isEditing} options={electricHeatKWOptions} value={report.electricHeatKwPerStage}
                onChangeCallback={(value) => onChangeCallback({ electricHeatKwPerStage: scrubNumber(value, true) })}
               />}
      />

      <ColumnGridPanel.Column.Item label="Airflow Input Mode" value={friendlyName(report.extendedIndoorAirflowInputMode, AIRFLOW_INPUT_MODE)} />
      <ColumnGridPanel.Column.Item label="Motor Horsepower" value={report.extendedIndoorMotorHorsepower} />

      {anyFurnace &&
        <>
          <ColumnGridPanel.Column.Item label="Control Type" value={friendlyName(report.extendedIndoorFurnaceConfig, CONTROL_TYPE)} />
          <ColumnGridPanel.Column.Item label="Unit Orientation Config" value={report.extendedIndoorFurnaceUnitOrientation} />
          <ColumnGridPanel.Column.Item label="Supply Air Sensor" value={report.extendedIndoorSupplyAirTempSensorStatus} />
          <ColumnGridPanel.Column.Item label="Return Air Sensor" value={report.extendedIndoorReturnAirTempSensorStatus} />
          <ColumnGridPanel.Column.Item label="Return Static Sensor" value={report.extendedIndoorReturnStaticPressureStatus} />
        </>
      }
      {hasValue(report.extendedIndoorAdjustedMinimumFiringRate) && <ColumnGridPanel.Column.Item label="Adjusted Min Firing Rate" value={percentage(report.extendedIndoorAdjustedMinimumFiringRate)} />}
      {hasValue(report.extendedIndoorAirflowSetting) && <ColumnGridPanel.Column.Item label="Airflow Setting" value={friendlyName(report.extendedIndoorAirflowSetting)} />}
      {hasValue(report.extendedIndoorFuelTypeSetting) && <ColumnGridPanel.Column.Item label="Fuel Type" value={friendlyName(report.extendedIndoorFuelTypeSetting)} />}
      {hasValue(report.extendedIndoorElevation) && <ColumnGridPanel.Column.Item label="Elevation" value={feet(report.extendedIndoorElevation)} />}
      {hasValue(report.extendedIndoorLowFireGasValveAdjustment) && <ColumnGridPanel.Column.Item label="Low Fire Gas Valve Adjustment" value={report.extendedIndoorLowFireGasValveAdjustment} />}
      {hasValue(report.extendedIndoorHighFireGasValveAdjustment) && <ColumnGridPanel.Column.Item label="High Fire Gas Valve Adjustment" value={report.extendedIndoorHighFireGasValveAdjustment} />}

    </ColumnGridPanel.Column>
  )
}

export default IndoorUnit
